const OKENDO_REVIEWS_LIMIT = 5;
const FETCH_TIMEOUT_MS = 5000;
const SCRIPT_SELECTOR = '[data-product-structured-data]';
const PATCHED_ATTRIBUTE = 'data-oke-reviews-patched';

const isNonEmptyString = (value) => typeof value === 'string' && value.trim() !== '';

const getSubscriberId = () => {
  const settingsElement = document.getElementById('oke-reviews-settings');
  if (!settingsElement) return '';

  try {
    const settings = JSON.parse(settingsElement.textContent || '');
    const subscriberId = settings?.subscriberId;

    return typeof subscriberId === 'string' ? subscriberId : '';
  } catch {
    return '';
  }
};

const toReviewSchema = (review) => {
  if (review?.status !== 'approved') return null;

  const name = review.reviewer?.displayName;
  const rating = Number(review.rating);

  if (!isNonEmptyString(name) || !Number.isFinite(rating)) return null;

  const schema = {
    '@type': 'Review',
    author: {
      '@type': 'Person',
      name: name.trim(),
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: rating,
      bestRating: 5,
      worstRating: 1,
    },
  };

  if (isNonEmptyString(review.dateCreated)) {
    schema.datePublished = review.dateCreated;
  }

  if (isNonEmptyString(review.body)) {
    schema.reviewBody = review.body;
  }

  if (isNonEmptyString(review.title)) {
    schema.name = review.title;
  }

  return schema;
};

const fetchProductReviews = async (subscriberId, productId) => {
  const url = `https://api.okendo.io/v1/stores/${encodeURIComponent(
    subscriberId
  )}/products/${encodeURIComponent(productId)}/reviews?limit=${OKENDO_REVIEWS_LIMIT}&orderBy=${encodeURIComponent(
    'date desc'
  )}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) return [];

    const data = await response.json();
    const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
    const schemaReviews = [];

    for (const review of reviews) {
      const schemaReview = toReviewSchema(review);
      if (!schemaReview) continue;

      schemaReviews.push(schemaReview);
      if (schemaReviews.length === OKENDO_REVIEWS_LIMIT) break;
    }

    return schemaReviews;
  } catch {
    return [];
  } finally {
    clearTimeout(timeoutId);
  }
};

const patchProductJsonLd = (scriptElement, reviews) => {
  if (scriptElement.hasAttribute(PATCHED_ATTRIBUTE)) return;

  scriptElement.setAttribute(PATCHED_ATTRIBUTE, '');
  if (!reviews.length) return;

  let product;

  try {
    product = JSON.parse(scriptElement.textContent || '');
  } catch {
    return;
  }

  if (!product || typeof product !== 'object' || product.review != null) return;

  product.review = reviews;

  try {
    scriptElement.textContent = JSON.stringify(product);
  } catch {
    // Leave the original JSON-LD in place if serialization fails.
  }
};

const enhanceProductStructuredData = async () => {
  const scriptElements = document.querySelectorAll(SCRIPT_SELECTOR);
  if (!scriptElements.length) return;

  const subscriberId = getSubscriberId();
  if (!subscriberId) return;

  for (const scriptElement of scriptElements) {
    if (scriptElement.hasAttribute(PATCHED_ATTRIBUTE)) continue;

    const productId = scriptElement.getAttribute('data-oke-reviews-product-id');
    if (!productId) continue;

    const reviews = await fetchProductReviews(subscriberId, productId);
    patchProductJsonLd(scriptElement, reviews);
  }
};

enhanceProductStructuredData();
